import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import ixenSystemService, { IxenPair } from '@/services/ixen_system.service'
import { Switch } from '@/components/ui/switch'

const PROVIDERS = [
  { value: 'FND', label: 'FundedNext', color: '#3b82f6' },
  { value: 'FPI', label: 'FundingPips', color: '#8b5cf6' },
  { value: 'FPR', label: 'FunderPro', color: '#f97316' },
  { value: 'FP2', label: 'FundingPips 2 Step', color: '#22c55e' },
]

const PHASES = [
  { value: 'challenge', label: 'Challenge' },
  { value: 'real_pre_payout', label: 'Pre Payout' },
  { value: 'real_post_payout', label: 'Post Payout' },
]
// FP2 ha fasi proprie
const PHASES_FP2 = [
  { value: 'one_step', label: 'One Step' },
  { value: 'two_step', label: 'Two Step' },
  { value: 'master', label: 'Master' },
]

const SIZES = [25000, 50000, 100000, 150000, 200000]
const SIZES_FP2 = [50000, 100000, 200000]

type Tab = 'connection' | 'propfirm' | 'engines' | 'token' | 'delete'

interface PairDetail extends IxenPair {
  prop_token: string | null
  broker_token: string | null
  prop_token_full: string | null
  broker_token_full: string | null
  cloud_url: string
  display_name_raw: string
}

interface Props {
  pairId: number
  onClose: () => void
  onUpdated: (pair: IxenPair) => void
  onDeleted: (pairId: number) => void
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button
      onClick={copy}
      className="text-[10px] font-bold px-2 py-1 rounded bg-[#1e3a5f] border border-[#38bdf8]/30 text-[#38bdf8] hover:bg-[#1e4a7f] transition-all flex-shrink-0"
    >
      {copied ? '✓ Copiato' : 'Copia'}
    </button>
  )
}

function TokenRow({ label, value, full }: { label: string; value: string | null; full: string | null }) {
  if (!value) return (
    <div className="flex items-center justify-between gap-2 py-2">
      <span className="text-[11px] text-[#64748b]">{label}</span>
      <span className="text-[11px] text-[#334155] italic">Non disponibile</span>
    </div>
  )
  return (
    <div className="flex items-center justify-between gap-2 py-2 border-b border-[#1e3a5f]/40 last:border-0">
      <div className="min-w-0">
        <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-0.5">{label}</div>
        <div className="font-mono text-xs text-[#94a3b8] truncate">{value}</div>
      </div>
      {full && <CopyButton text={full} />}
    </div>
  )
}

function InputField({
  label, value, onChange, type = 'text', placeholder = '',
}: {
  label: string; value: string; onChange: (v: string) => void
  type?: string; placeholder?: string
}) {
  return (
    <div>
      <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none focus:border-[#38bdf8] transition-colors"
      />
    </div>
  )
}

export function PairManagementModal({ pairId, onClose, onUpdated, onDeleted }: Props) {
  const [tab, setTab] = useState<Tab>('connection')
  const [detail, setDetail] = useState<PairDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [propLogin, setPropLogin] = useState('')
  const [propServer, setPropServer] = useState('')
  const [brokerLogin, setBrokerLogin] = useState('')
  const [brokerServer, setBrokerServer] = useState('')
  const [displayName, setDisplayName] = useState('')

  const [provider, setProvider] = useState('FND')
  const [phase, setPhase] = useState('challenge')
  const [size, setSize] = useState(100000)
  const [controlMode, setControlMode] = useState(false)
  const [disciplinedMode, setDisciplinedMode] = useState(false)
  // Profilo rischio FP2: settabile SOLO se il backend non lo ha ancora (poi immutabile)
  const [fp2Profile, setFp2Profile] = useState<'conservativo' | 'aggressivo'>('conservativo')
  const fp2ProfileLocked = !!detailFp2Profile()

  function detailFp2Profile() {
    return detail?.fp2_risk_profile ?? null
  }

  // Cambio provider: riallinea fase e capitale ai valori validi
  function handleProviderChange(prov: string) {
    setProvider(prov)
    const phases = prov === 'FP2' ? PHASES_FP2 : PHASES
    const sizes = prov === 'FP2' ? SIZES_FP2 : SIZES
    if (!phases.some(p => p.value === phase)) setPhase(phases[0].value)
    if (!sizes.includes(size)) setSize(sizes.includes(100000) ? 100000 : sizes[0])
  }

  // ── Motori: auto-exec Yomi + Sempai, per tutti gli abbonati ──
  const [yomiMode, setYomiMode] = useState<'manual' | 'auto'>('manual')
  const [yomiMinClass, setYomiMinClass] = useState<'STRONG' | 'GOOD' | 'WEAK'>('STRONG')
  const [sempaiAuto, setSempaiAuto] = useState(false)
  const [enginesLoaded, setEnginesLoaded] = useState(false)
  const [savingEngines, setSavingEngines] = useState(false)
  // Personalizzazione auto per motore (valori in PERCENTO nella UI, es. "1.2")
  const [autoSl, setAutoSl] = useState('')
  const [autoTp, setAutoTp] = useState('')
  const [sempaiSl, setSempaiSl] = useState('')
  const [sempaiTp, setSempaiTp] = useState('')
  // Makatsu: SL/TP per coppia (aperto a tutti, 17/08/2026)
  const [makatsuSl, setMakatsuSl] = useState('')
  const [makatsuTp, setMakatsuTp] = useState('')
  // Toggle attivazione Makatsu (spostato qui dalla card coppia, 17/08/2026)
  const [makatsuLocal, setMakatsuLocal] = useState<boolean | null>(null)
  const [savingMakatsu, setSavingMakatsu] = useState(false)
  const makatsuOn = makatsuLocal ?? !!(detail as any)?.makatsu_enabled

  async function handleToggleMakatsu(next: boolean) {
    if (savingMakatsu) return
    setSavingMakatsu(true)
    try {
      const updated = await ixenSystemService.updatePair(pairId, { makatsu_enabled: next })
      setMakatsuLocal(next)
      toast.success(next ? 'Motore Makatsu attivato' : 'Motore Makatsu disattivato', {
        description: next
          ? 'I segnali Makatsu apriranno in automatico su questa coppia (regole standard).'
          : 'Questa coppia non riceverà più aperture dai segnali Makatsu.',
      })
      onUpdated(updated)
    } catch (err: any) {
      toast.error('Errore salvataggio Makatsu', { description: err?.message ?? 'Riprova.' })
    } finally {
      setSavingMakatsu(false)
    }
  }
  const [winStart, setWinStart] = useState('')
  const [winEnd, setWinEnd] = useState('')
  const [savingPrefs, setSavingPrefs] = useState(false)

  // Validazione live SL/TP auto (valori UI in percento; vuoto = default, valido).
  function autoPctError(raw: string, min: number, max: number): string | null {
    if (raw.trim() === '') return null
    const v = parseFloat(raw.replace(',', '.'))
    if (isNaN(v)) return 'Valore non valido'
    if (v < min || v > max) return `Consentito ${String(min).replace('.', ',')} – ${String(max).replace('.', ',')}%`
    return null
  }
  const autoSlError = autoPctError(autoSl, 1, 1.2)
  const autoTpError = autoPctError(autoTp, 1, 4)
  const sempaiSlError = autoPctError(sempaiSl, 1, 1.2)
  const sempaiTpError = autoPctError(sempaiTp, 1, 4)
  const makatsuSlError = autoPctError(makatsuSl, 1, 1.2)
  const makatsuTpError = autoPctError(makatsuTp, 1, 4)
  const hasAutoPrefsErrors = !!(autoSlError || autoTpError || sempaiSlError || sempaiTpError || makatsuSlError || makatsuTpError)

  const [newTokens, setNewTokens] = useState<{ prop_token: string | null; broker_token: string | null } | null>(null)
  const [regenConfirm, setRegenConfirm] = useState(false)

  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleting, setDeleting] = useState(false)

  const [resetLossConfirm, setResetLossConfirm] = useState(false)
  const [resettingLoss, setResettingLoss] = useState(false)

  useEffect(() => {
    ixenSystemService.getPairDetail(pairId).then(d => {
      setDetail(d)
      setPropLogin(d.prop_login)
      setPropServer(d.prop_server)
      setBrokerLogin(d.broker_login)
      setBrokerServer(d.broker_server)
      setDisplayName(d.display_name_raw)
      setProvider(d.prop_provider)
      setPhase(d.prop_phase)
      setSize(d.prop_size)
      setControlMode(!!d.control_mode)
      setDisciplinedMode(!!d.fnd_disciplined_mode)
      if (d.fp2_risk_profile) setFp2Profile(d.fp2_risk_profile)
    }).catch(() => toast.error('Errore caricamento dati coppia'))
      .finally(() => setLoading(false))
  }, [pairId])

  // Carica le impostazioni motori (tab MOTORI, per tutti gli utenti).
  useEffect(() => {
    let alive = true
    ixenSystemService.getYomiSettings(pairId)
      .then(s => {
        if (!alive) return
        setYomiMode(s.mode ?? 'manual')
        setYomiMinClass((s.yomi_min_class as 'STRONG' | 'GOOD' | 'WEAK') ?? 'STRONG')
        setSempaiAuto(!!s.sempai_auto)
        setAutoSl(s.auto_sl_pct != null ? String(+(s.auto_sl_pct * 100).toFixed(2)) : '')
        setAutoTp(s.auto_tp_pct != null ? String(+(s.auto_tp_pct * 100).toFixed(2)) : '')
        setSempaiSl(s.sempai_auto_sl_pct != null ? String(+(s.sempai_auto_sl_pct * 100).toFixed(2)) : '')
        setSempaiTp(s.sempai_auto_tp_pct != null ? String(+(s.sempai_auto_tp_pct * 100).toFixed(2)) : '')
        setMakatsuSl(s.makatsu_auto_sl_pct != null ? String(+(s.makatsu_auto_sl_pct * 100).toFixed(2)) : '1.2')
        setMakatsuTp(s.makatsu_auto_tp_pct != null ? String(+(s.makatsu_auto_tp_pct * 100).toFixed(2)) : '1.6')
        setWinStart(s.auto_window_start ?? '')
        setWinEnd(s.auto_window_end ?? '')
        setEnginesLoaded(true)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [pairId])

  async function saveEngines(
    patch: { mode?: 'manual' | 'auto'; yomi_min_class?: 'STRONG' | 'GOOD' | 'WEAK'; sempai_auto?: boolean },
    optimistic: () => void,
    rollback: () => void,
    successMsg: string,
  ) {
    optimistic()
    setSavingEngines(true)
    const nextMode = patch.mode ?? yomiMode
    try {
      await ixenSystemService.updateYomiSettings(pairId, {
        mode: nextMode,
        accepted: nextMode === 'auto',
        ...patch,
      })
      toast.success(successMsg)
    } catch (err: any) {
      rollback()
      toast.error('Errore salvataggio', { description: err?.response?.data?.detail ?? err?.message ?? '' })
    } finally { setSavingEngines(false) }
  }

  // Salva SL/TP auto per motore (Yomi + Sempai) + finestra oraria (valori UI in percento).
  async function handleSaveAutoPrefs() {
    const sl = autoSl.trim() === '' ? 0 : parseFloat(autoSl.replace(',', '.'))
    const tp = autoTp.trim() === '' ? 0 : parseFloat(autoTp.replace(',', '.'))
    const sSl = sempaiSl.trim() === '' ? 0 : parseFloat(sempaiSl.replace(',', '.'))
    const sTp = sempaiTp.trim() === '' ? 0 : parseFloat(sempaiTp.replace(',', '.'))
    const mkSl = makatsuSl.trim() === '' ? 0 : parseFloat(makatsuSl.replace(',', '.'))
    const mkTp = makatsuTp.trim() === '' ? 0 : parseFloat(makatsuTp.replace(',', '.'))
    if (mkSl !== 0 && (isNaN(mkSl) || mkSl < 1 || mkSl > 1.2)) {
      toast.error('SL automatico Makatsu fuori range', { description: 'Consentito da 1% a 1,2%. Lascia vuoto per usare il default (1,2%).' })
      return
    }
    if (mkTp !== 0 && (isNaN(mkTp) || mkTp < 1 || mkTp > 4)) {
      toast.error('TP automatico Makatsu fuori range', { description: 'Consentito da 1% a 4%. Lascia vuoto per usare il default (1,6%).' })
      return
    }
    if (sl !== 0 && (isNaN(sl) || sl < 1 || sl > 1.2)) {
      toast.error('SL automatico Yomi fuori range', { description: 'Consentito da 1% a 1,2%. Lascia vuoto per usare il default.' })
      return
    }
    if (tp !== 0 && (isNaN(tp) || tp < 1 || tp > 4)) {
      toast.error('TP automatico Yomi fuori range', { description: 'Consentito da 1% a 4%. Lascia vuoto per usare il default.' })
      return
    }
    if (sSl !== 0 && (isNaN(sSl) || sSl < 1 || sSl > 1.2)) {
      toast.error('SL automatico Sempai fuori range', { description: 'Consentito da 1% a 1,2%. Lascia vuoto per usare il default.' })
      return
    }
    if (sTp !== 0 && (isNaN(sTp) || sTp < 1 || sTp > 4)) {
      toast.error('TP automatico Sempai fuori range', { description: 'Consentito da 1% a 4%. Lascia vuoto per usare il default.' })
      return
    }
    if ((winStart === '') !== (winEnd === '')) {
      toast.error('Finestra oraria incompleta', { description: 'Imposta sia inizio sia fine, oppure lascia entrambi vuoti.' })
      return
    }
    setSavingPrefs(true)
    try {
      await ixenSystemService.updateYomiSettings(pairId, {
        mode: yomiMode,
        accepted: yomiMode === 'auto',
        auto_sl_pct: sl === 0 ? 0 : Math.round(sl * 1000) / 100000,
        auto_tp_pct: tp === 0 ? 0 : Math.round(tp * 1000) / 100000,
        sempai_auto_sl_pct: sSl === 0 ? 0 : Math.round(sSl * 1000) / 100000,
        sempai_auto_tp_pct: sTp === 0 ? 0 : Math.round(sTp * 1000) / 100000,
        makatsu_auto_sl_pct: mkSl === 0 ? 0 : Math.round(mkSl * 1000) / 100000,
        makatsu_auto_tp_pct: mkTp === 0 ? 0 : Math.round(mkTp * 1000) / 100000,
        auto_window_start: winStart,
        auto_window_end: winEnd,
        auto_window_tz: winStart ? (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC') : '',
      })
      toast.success('Personalizzazione auto salvata')
    } catch (err: any) {
      toast.error('Errore salvataggio', { description: err?.response?.data?.detail ?? err?.message ?? '' })
    } finally { setSavingPrefs(false) }
  }

  async function handleSaveConnection() {
    setSaving(true)
    try {
      const updated = await ixenSystemService.updatePairFull(pairId, {
        display_name: displayName,
        prop_login: propLogin,
        prop_server: propServer,
        broker_login: brokerLogin,
        broker_server: brokerServer,
      })
      onUpdated(updated)
      if (updated.account_changed) {
        toast.success('Connessione aggiornata', { description: 'Gli EA si riconnetteranno automaticamente entro 30 secondi.' })
      } else {
        toast.success('Impostazioni salvate')
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Errore salvataggio')
    } finally { setSaving(false) }
  }

  async function handleSavePropFirm() {
    setSaving(true)
    try {
      const updated = await ixenSystemService.updatePairFull(pairId, {
        prop_provider: provider, prop_phase: phase, prop_size: size,
        control_mode: controlMode, fnd_disciplined_mode: disciplinedMode,
        // Profilo FP2: inviato solo se non ancora fissato lato backend (poi immutabile)
        ...(provider === 'FP2' && !fp2ProfileLocked ? { fp2_risk_profile: fp2Profile } : {}),
      })
      onUpdated(updated)
      toast.success('Prop firm aggiornata')
    } catch (err: any) {
      toast.error(err?.message ?? 'Errore salvataggio')
    } finally { setSaving(false) }
  }

  async function handleResetDailyLoss() {
    setResettingLoss(true)
    try {
      const res = await ixenSystemService.resetDailyLoss(pairId)
      toast.success('Daily-loss azzerato', { description: res.message })
      setResetLossConfirm(false)
    } catch (err: any) {
      toast.error('Errore reset daily-loss', { description: err?.detail ?? err?.message ?? '' })
    } finally { setResettingLoss(false) }
  }

  async function handleRegenerateTokens() {
    setSaving(true)
    setRegenConfirm(false)
    try {
      const res = await ixenSystemService.regenerateTokens(pairId)
      setNewTokens(res)
      toast.warning('Token rigenerati — aggiorna gli EA prima di riattivarli')
    } catch (err: any) {
      toast.error(err?.message ?? 'Errore rigenerazione token')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!detail || deleteConfirmName !== detail.display_name) return
    setDeleting(true)
    try {
      await ixenSystemService.deletePair(pairId)
      onDeleted(pairId)
      onClose()
      toast.success('Coppia eliminata')
    } catch (err: any) {
      toast.error(err?.message ?? 'Errore eliminazione')
    } finally { setDeleting(false) }
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'connection', label: 'CONNESSIONE' },
    { id: 'propfirm', label: 'PROP FIRM' },
    { id: 'engines', label: 'MOTORI' },
    { id: 'token', label: 'TOKEN EA' },
    { id: 'delete', label: 'ELIMINA' },
  ]

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-[#0d1529] border border-[#1e3a5f] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#1e3a5f]">
          <div>
            <div className="font-black text-white text-base">Gestisci coppia</div>
            {detail && (
              <div className="text-[11px] text-[#64748b] mt-0.5">
                {detail.display_name} · {detail.prop_provider_label} ${detail.prop_size.toLocaleString()}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-white transition-colors text-lg leading-none ml-4">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#1e3a5f]">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-2.5 text-[10px] font-bold tracking-widest transition-all ${
                tab === t.id
                  ? t.id === 'delete'
                    ? 'text-red-400 border-b-2 border-red-500'
                    : 'text-[#38bdf8] border-b-2 border-[#38bdf8]'
                  : 'text-[#475569] hover:text-[#94a3b8]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-10 text-[#38bdf8] text-sm animate-pulse">Caricamento...</div>
          ) : !detail ? (
            <div className="text-center py-10 text-red-400 text-sm">Errore caricamento dati</div>
          ) : (

            <>
              {/* ── Tab: Connessione ── */}
              {tab === 'connection' && (
                <div className="space-y-4">
                  <InputField label="Nome coppia" value={displayName} onChange={setDisplayName} placeholder="es. Test1Prop" />

                  <div className="bg-[#0a0f1e] rounded-xl p-3.5 border border-[#1e3a5f]/60 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black bg-green-900/40 text-green-400 border border-green-700/50 px-2 py-0.5 rounded">MT5 · PROP</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${detail.ea_prop.connected ? 'bg-green-400 shadow-[0_0_4px_#4ade80]' : 'bg-red-500'}`} />
                        <span className={`text-[9px] font-bold ${detail.ea_prop.connected ? 'text-green-400' : 'text-red-400'}`}>
                          {detail.ea_prop.connected ? 'Connesso' : 'Offline'} · {detail.prop_server}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <InputField label="Numero conto" value={propLogin} onChange={setPropLogin} placeholder="5012847" />
                      <InputField label="Password" value="••••••••" onChange={() => {}} type="password" placeholder="••••••••" />
                    </div>
                    <InputField label="Server" value={propServer} onChange={setPropServer} placeholder="FunderPro-Live" />
                  </div>

                  <div className="bg-[#0a0f1e] rounded-xl p-3.5 border border-[#1e3a5f]/60 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-black bg-blue-900/40 text-blue-400 border border-blue-700/50 px-2 py-0.5 rounded">MT4 · BROKER</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${detail.ea_broker.connected ? 'bg-green-400 shadow-[0_0_4px_#4ade80]' : 'bg-red-500'}`} />
                        <span className={`text-[9px] font-bold ${detail.ea_broker.connected ? 'text-green-400' : 'text-red-400'}`}>
                          {detail.ea_broker.connected ? 'Connesso' : 'Offline'} · {detail.broker_server}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <InputField label="Numero conto" value={brokerLogin} onChange={setBrokerLogin} placeholder="7294011" />
                      <InputField label="Password" value="••••••••" onChange={() => {}} type="password" placeholder="••••••••" />
                    </div>
                    <InputField label="Server" value={brokerServer} onChange={setBrokerServer} placeholder="Exness-Real4" />
                  </div>

                  <div className="bg-[#0f2a1a] border border-green-900/40 rounded-lg px-3 py-2 text-[10px] text-green-400/80">
                    Dopo aver salvato, gli EA si riconnetteranno automaticamente entro 30 secondi.
                  </div>
                </div>
              )}

              {/* ── Tab: Prop Firm ── */}
              {tab === 'propfirm' && (
                <div className="space-y-5">
                  <div>
                    <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">Provider</div>
                    <div className="grid grid-cols-3 gap-2">
                      {PROVIDERS.map(p => (
                        <button
                          key={p.value}
                          onClick={() => handleProviderChange(p.value)}
                          style={provider === p.value ? { borderColor: p.color, color: p.color } : {}}
                          className={`py-3 px-2 rounded-xl border text-center transition-all ${
                            provider === p.value
                              ? 'bg-[#1e3a5f]/60'
                              : 'border-[#1e3a5f] text-[#475569] hover:border-[#38bdf8]/40 hover:text-[#94a3b8]'
                          }`}
                        >
                          <div className="text-xs font-black">{p.label}</div>
                          <div className="text-[9px] opacity-60 mt-0.5">{p.value}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">Fase account</div>
                    <div className="flex gap-2">
                      {(provider === 'FP2' ? PHASES_FP2 : PHASES).map(ph => (
                        <button
                          key={ph.value}
                          onClick={() => setPhase(ph.value)}
                          className={`flex-1 py-2 rounded-lg text-[11px] font-bold border transition-all ${
                            phase === ph.value
                              ? 'bg-[#1e3a5f] border-[#38bdf8] text-[#38bdf8]'
                              : 'bg-[#0a0f1e] border-[#1e3a5f] text-[#475569] hover:border-[#38bdf8]/40'
                          }`}
                        >
                          {ph.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">Capitale account</div>
                    <div className="grid grid-cols-3 gap-2">
                      {(provider === 'FP2' ? SIZES_FP2 : SIZES).map(s => (
                        <button
                          key={s}
                          onClick={() => setSize(s)}
                          className={`py-2 rounded-lg text-xs font-bold border transition-all ${
                            size === s
                              ? 'bg-[#1e3a5f] border-[#38bdf8] text-[#38bdf8]'
                              : 'bg-[#0a0f1e] border-[#1e3a5f] text-[#475569] hover:border-[#38bdf8]/40'
                          }`}
                        >
                          ${(s / 1000).toFixed(0)}K
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── Profilo rischio FP2 (settabile solo se non ancora fissato) ── */}
                  {provider === 'FP2' && (
                    <div>
                      <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">
                        Profilo rischio {fp2ProfileLocked && <span className="text-[#334155] normal-case font-normal">(immutabile)</span>}
                      </div>
                      <div className="flex gap-2">
                        {(['conservativo', 'aggressivo'] as const).map(p => (
                          <button
                            key={p}
                            disabled={fp2ProfileLocked && fp2Profile !== p}
                            onClick={() => { if (!fp2ProfileLocked) setFp2Profile(p) }}
                            className={`flex-1 py-2 rounded-lg text-[11px] font-bold border transition-all ${
                              fp2Profile === p
                                ? 'bg-[#1e3a5f] border-[#38bdf8] text-[#38bdf8]'
                                : 'bg-[#0a0f1e] border-[#1e3a5f] text-[#475569]'
                            } ${fp2ProfileLocked && fp2Profile !== p ? 'opacity-40 cursor-not-allowed' : ''}`}
                          >
                            {p === 'conservativo' ? 'Conservativo (17% / 34%)' : 'Aggressivo (20% / 40%)'}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-[#475569] mt-1">
                        {fp2ProfileLocked
                          ? 'Il profilo è stato fissato alla creazione e non può essere modificato.'
                          : 'Scegli il profilo: verrà fissato al salvataggio e non sarà più modificabile. (Master: copertura 70% fissa)'}
                      </p>
                    </div>
                  )}

                  {/* ── Modalità Controllo (prop-only, fire-and-forget) ── */}
                  <div className="pt-1">
                    <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">Modalità Controllo</div>
                    <button
                      onClick={() => setControlMode(v => !v)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border transition-all text-left ${
                        controlMode
                          ? 'bg-[#3a2a0f]/60 border-amber-500/60'
                          : 'bg-[#0a0f1e] border-[#1e3a5f] hover:border-[#38bdf8]/40'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className={`text-xs font-black ${controlMode ? 'text-amber-400' : 'text-[#94a3b8]'}`}>
                          {controlMode ? 'Attiva — solo PROP (MT5)' : 'Disattivata — hedge PROP + BROKER'}
                        </div>
                        <div className="text-[10px] text-[#64748b] mt-0.5 leading-snug">
                          Invia l'ordine solo all'EA PROP con SL/TP/leva della card. Nessun broker, nessun tracking né chiusura automatica: gestisci tu la posizione.
                        </div>
                      </div>
                      <div className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${controlMode ? 'bg-amber-500' : 'bg-[#1e3a5f]'}`}>
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${controlMode ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                      </div>
                    </button>
                  </div>

                  {/* ── Modalità Disciplinata (solo FundedNext) ── */}
                  {provider === 'FND' && (
                    <div className="pt-1">
                      <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">Modalità Disciplinata</div>
                      <button
                        onClick={() => setDisciplinedMode(v => !v)}
                        className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border transition-all text-left ${
                          disciplinedMode
                            ? 'bg-[#0f2a3a]/60 border-[#38bdf8]/60'
                            : 'bg-[#0a0f1e] border-[#1e3a5f] hover:border-[#38bdf8]/40'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className={`text-xs font-black ${disciplinedMode ? 'text-[#38bdf8]' : 'text-[#94a3b8]'}`}>
                            {disciplinedMode ? 'Attiva — lotti ridotti' : 'Disattivata — sizing standard'}
                          </div>
                          <div className="text-[10px] text-[#64748b] mt-0.5 leading-snug">
                            Limita il margine PROP al 29% del valore nominale in tutte le fasi: lotti più piccoli e rischio per operazione contenuto. La copertura sul broker resta invariata.
                          </div>
                        </div>
                        <div className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${disciplinedMode ? 'bg-[#38bdf8]' : 'bg-[#1e3a5f]'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${disciplinedMode ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                        </div>
                      </button>
                    </div>
                  )}

                  {/* ── Reset daily-loss (prop nuova dopo reset provider) ── */}
                  <div className="pt-1">
                    <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">Reset daily-loss</div>
                    <div className="bg-[#0a0f1e] border border-[#1e3a5f] rounded-xl p-3 space-y-2">
                      <p className="text-[10px] text-[#94a3b8] leading-snug">
                        Se hai bruciato la prop al limite giornaliero e hai comprato una prop <b>nuova</b> con lo stesso numero di conto,
                        azzera il daily-loss di oggi per ripartire a operare. Le operazioni precedenti <b>restano nello storico</b>: nessuna cancellazione.
                      </p>
                      {!resetLossConfirm ? (
                        <button
                          onClick={() => setResetLossConfirm(true)}
                          className="w-full py-2 rounded-lg border border-amber-700 bg-amber-900/20 text-amber-300 text-[11px] font-black hover:bg-amber-900/40 transition-all"
                        >
                          ↺ Azzera daily-loss di oggi
                        </button>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-[10px] text-amber-400 font-bold">Confermi l'azzeramento del daily-loss per oggi?</p>
                          <div className="flex gap-2">
                            <button onClick={() => setResetLossConfirm(false)} className="flex-1 py-2 rounded-lg border border-[#1e3a5f] text-[#64748b] text-xs font-bold hover:bg-[#1e3a5f]/30 transition-all">Annulla</button>
                            <button onClick={handleResetDailyLoss} disabled={resettingLoss} className="flex-1 py-2 rounded-lg bg-amber-900/50 border border-amber-700 text-amber-300 text-xs font-black hover:bg-amber-900/70 disabled:opacity-40 transition-all">
                              {resettingLoss ? 'Azzeramento...' : 'Conferma reset'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Tab: Motori (Yomi + Sempai, per tutti gli abbonati) ── */}
              {tab === 'engines' && (
                <div className="space-y-5">
                  {!enginesLoaded ? (
                    <div className="text-center py-10 text-[#38bdf8] text-sm animate-pulse">Caricamento impostazioni motori...</div>
                  ) : (
                    <>
                      {/* ── Finestra oraria auto (condivisa Yomi + Sempai) ── */}
                      <div className="bg-[#0a0f1e] border border-[#1e3a5f] rounded-xl p-3">
                        <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">⏰ Finestra oraria auto (Yomi + Sempai)</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">Dalle</label>
                            <input
                              type="time"
                              value={winStart}
                              onChange={e => setWinStart(e.target.value)}
                              className="w-full bg-[#0d1526] border border-[#1e3a5f] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#38bdf8] transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">Alle</label>
                            <input
                              type="time"
                              value={winEnd}
                              onChange={e => setWinEnd(e.target.value)}
                              className="w-full bg-[#0d1526] border border-[#1e3a5f] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#38bdf8] transition-colors"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-1.5">
                          <div className="text-[10px] text-[#64748b] leading-snug">
                            Vale per tutte le aperture automatiche. Orari nel tuo fuso ({Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}). Vuoto = sempre attivo.
                          </div>
                          {(winStart || winEnd) && (
                            <button
                              onClick={() => { setWinStart(''); setWinEnd('') }}
                              className="text-[10px] font-bold text-[#38bdf8] hover:underline flex-shrink-0 ml-2"
                            >
                              Azzera
                            </button>
                          )}
                        </div>
                      </div>

                      {/* ══ Sezione YOMI ══ */}
                      <div className={`rounded-xl border p-3 space-y-4 transition-colors ${yomiMode === 'auto' ? 'border-green-500/40 bg-[#0f2a1a]/20' : 'border-[#1e3a5f] bg-[#0a0f1e]/40'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] font-black uppercase tracking-widest ${yomiMode === 'auto' ? 'text-green-400' : 'text-[#94a3b8]'}`}>Motore Yomi</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${yomiMode === 'auto' ? 'bg-green-900/40 text-green-400 border-green-700/50' : 'bg-[#1e3a5f]/40 text-[#64748b] border-[#1e3a5f]'}`}>
                            {yomiMode === 'auto' ? 'AUTO ATTIVO' : 'MANUALE'}
                          </span>
                        </div>

                        {/* Interruttore auto-esecuzione Yomi */}
                        <button
                          disabled={savingEngines}
                          onClick={() => {
                            const next = yomiMode === 'auto' ? 'manual' : 'auto'
                            const prev = yomiMode
                            saveEngines(
                              { mode: next },
                              () => setYomiMode(next),
                              () => setYomiMode(prev),
                              next === 'auto' ? 'Auto-esecuzione Yomi attiva' : 'Auto-esecuzione Yomi disattivata',
                            )
                          }}
                          className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl border transition-all text-left disabled:opacity-50 ${
                            yomiMode === 'auto'
                              ? 'bg-[#0f2a1a]/60 border-green-500/60'
                              : 'bg-[#0a0f1e] border-[#1e3a5f] hover:border-[#38bdf8]/40'
                          }`}
                        >
                          <div className="min-w-0">
                            <div className={`text-xs font-black ${yomiMode === 'auto' ? 'text-green-400' : 'text-[#94a3b8]'}`}>
                              {yomiMode === 'auto' ? 'Attiva — i segnali Yomi vengono eseguiti da soli' : 'Disattivata — esecuzione manuale'}
                            </div>
                            <div className="text-[10px] text-[#64748b] mt-0.5 leading-snug">
                              Quando attiva, iXen apre in automatico i segnali Yomi che rispettano la qualità minima scelta sotto.
                            </div>
                          </div>
                          <div className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${yomiMode === 'auto' ? 'bg-green-500' : 'bg-[#1e3a5f]'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${yomiMode === 'auto' ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                          </div>
                        </button>

                        {/* Qualità minima Yomi */}
                        <div className={yomiMode === 'auto' ? '' : 'opacity-50 pointer-events-none'}>
                          <div className="text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-2">Qualità minima auto</div>
                        <div className="flex gap-2">
                          {(['STRONG', 'GOOD', 'WEAK'] as const).map(c => (
                            <button
                              key={c}
                              disabled={savingEngines || yomiMode !== 'auto'}
                              onClick={() => {
                                const prev = yomiMinClass
                                saveEngines(
                                  { yomi_min_class: c },
                                  () => setYomiMinClass(c),
                                  () => setYomiMinClass(prev),
                                  `Qualità minima: ${c}`,
                                )
                              }}
                              className={`flex-1 py-2 rounded-lg text-[11px] font-bold border transition-all disabled:opacity-50 ${
                                yomiMinClass === c
                                  ? 'bg-[#1e3a5f] border-[#38bdf8] text-[#38bdf8]'
                                  : 'bg-[#0a0f1e] border-[#1e3a5f] text-[#475569] hover:border-[#38bdf8]/40'
                              }`}
                            >
                              {c === 'STRONG' ? 'STRONG' : c === 'GOOD' ? 'GOOD+' : 'WEAK+'}
                            </button>
                          ))}
                        </div>
                        <div className="text-[10px] text-[#64748b] mt-1.5 leading-snug">
                          STRONG = solo segnali premium · GOOD+ = buoni e premium · WEAK+ = tutti i segnali.
                        </div>
                        </div>

                        {/* SL/TP aperture automatiche Yomi */}
                        <div className="pt-3 border-t border-green-500/20">
                          <div className="text-[10px] text-green-400/80 font-bold uppercase tracking-wider mb-2">SL / TP aperture automatiche Yomi</div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">SL % (1 – 1,2)</label>
                              <input
                                type="number" min={1} max={1.2} step={0.05}
                                value={autoSl}
                                onChange={e => setAutoSl(e.target.value)}
                                placeholder="Default"
                                className={`w-full bg-[#0a0f1e] border rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none transition-colors ${autoSlError ? 'border-red-500 focus:border-red-500' : 'border-[#1e3a5f] focus:border-[#38bdf8]'}`}
                              />
                              {autoSlError && <div className="text-[10px] text-red-400 mt-1">{autoSlError}</div>}
                            </div>
                            <div>
                              <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">TP % (1 – 4)</label>
                              <input
                                type="number" min={1} max={4} step={0.1}
                                value={autoTp}
                                onChange={e => setAutoTp(e.target.value)}
                                placeholder="Default"
                                className={`w-full bg-[#0a0f1e] border rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none transition-colors ${autoTpError ? 'border-red-500 focus:border-red-500' : 'border-[#1e3a5f] focus:border-[#38bdf8]'}`}
                              />
                              {autoTpError && <div className="text-[10px] text-red-400 mt-1">{autoTpError}</div>}
                            </div>
                          </div>
                          <div className="text-[10px] text-[#64748b] mt-1.5 leading-snug">
                            Vuoto = valori standard del sistema. Il limite giornaliero di rischio resta sempre attivo.
                          </div>
                        </div>
                      </div>

                      {/* ══ Sezione SEMPAI ══ */}
                      <div className={`rounded-xl border p-3 space-y-4 transition-colors ${sempaiAuto ? 'border-violet-500/40 bg-[#1a1030]/30' : 'border-[#1e3a5f] bg-[#0a0f1e]/40'}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] font-black uppercase tracking-widest ${sempaiAuto ? 'text-violet-400' : 'text-[#94a3b8]'}`}>Motore Sempai</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${sempaiAuto ? 'bg-violet-900/40 text-violet-300 border-violet-700/50' : 'bg-[#1e3a5f]/40 text-[#64748b] border-[#1e3a5f]'}`}>
                            {sempaiAuto ? 'AUTO ATTIVO' : 'MANUALE'}
                          </span>
                        </div>

                        {/* Interruttore Sempai auto-execute */}
                        <div className={`flex items-center justify-between gap-3 px-3 py-3 rounded-xl border transition-all ${sempaiAuto ? 'bg-[#1a1030]/60 border-violet-500/60' : 'bg-[#0a0f1e] border-[#1e3a5f]'}`}>
                          <div className="min-w-0">
                            <div className={`text-xs font-black ${sempaiAuto ? 'text-violet-300' : 'text-[#94a3b8]'}`}>
                              {sempaiAuto ? 'Attiva — gli alert Sempai vengono eseguiti da soli' : 'Disattivata — solo notifiche'}
                            </div>
                            <div className="text-[10px] text-[#64748b] mt-0.5 leading-snug">
                              Esegue gli alert esterni Sempai entro ±3$ dal prezzo corrente. Richiede anche l'attivazione globale lato sistema.
                            </div>
                          </div>
                          <Switch
                            checked={sempaiAuto}
                            disabled={savingEngines}
                            onCheckedChange={next => {
                              const prev = sempaiAuto
                              saveEngines(
                                { sempai_auto: next },
                                () => setSempaiAuto(next),
                                () => setSempaiAuto(prev),
                                next ? 'Sempai auto-execute attivo' : 'Sempai auto-execute disattivato',
                              )
                            }}
                          />
                        </div>

                        {/* SL/TP aperture automatiche Sempai */}
                        <div className="pt-3 border-t border-violet-500/20">
                          <div className="text-[10px] text-violet-400/80 font-bold uppercase tracking-wider mb-2">SL / TP aperture automatiche Sempai</div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">SL % (1 – 1,2)</label>
                              <input
                                type="number" min={1} max={1.2} step={0.05}
                                value={sempaiSl}
                                onChange={e => setSempaiSl(e.target.value)}
                                placeholder="Default"
                                className={`w-full bg-[#0a0f1e] border rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none transition-colors ${sempaiSlError ? 'border-red-500 focus:border-red-500' : 'border-[#1e3a5f] focus:border-[#38bdf8]'}`}
                              />
                              {sempaiSlError && <div className="text-[10px] text-red-400 mt-1">{sempaiSlError}</div>}
                            </div>
                            <div>
                              <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">TP % (1 – 4)</label>
                              <input
                                type="number" min={1} max={4} step={0.1}
                                value={sempaiTp}
                                onChange={e => setSempaiTp(e.target.value)}
                                placeholder="Default"
                                className={`w-full bg-[#0a0f1e] border rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none transition-colors ${sempaiTpError ? 'border-red-500 focus:border-red-500' : 'border-[#1e3a5f] focus:border-[#38bdf8]'}`}
                              />
                              {sempaiTpError && <div className="text-[10px] text-red-400 mt-1">{sempaiTpError}</div>}
                            </div>
                          </div>
                          <div className="text-[10px] text-[#64748b] mt-1.5 leading-snug">
                            Vuoto = default Sempai del sistema (SL 1,2% · TP 2%).
                          </div>
                        </div>
                      </div>

                      {/* ══ Sezione MAKATSU ══ */}
                      {(
                        <div className={`rounded-xl border p-3 space-y-4 transition-colors ${makatsuOn ? 'border-rose-500/40 bg-[#1a0f14]/30' : 'border-[#1e3a5f] bg-[#0a0f1e]/40'}`}>
                          <div className="flex items-center gap-2">
                            <span className={`text-[11px] font-black uppercase tracking-widest ${makatsuOn ? 'text-rose-400' : 'text-[#94a3b8]'}`}>Motore Makatsu</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${makatsuOn ? 'bg-rose-900/40 text-rose-300 border-rose-700/50' : 'bg-[#1e3a5f]/40 text-[#64748b] border-[#1e3a5f]'}`}>
                              {makatsuOn ? 'AUTO ATTIVO' : 'SPENTO'}
                            </span>
                          </div>

                          {/* Interruttore Makatsu (come Yomi/Sempai) */}
                          <div className={`flex items-center justify-between gap-3 px-3 py-3 rounded-xl border transition-all ${makatsuOn ? 'bg-[#1a0f14]/60 border-rose-500/60' : 'bg-[#0a0f1e] border-[#1e3a5f]'}`}>
                            <div className="min-w-0">
                              <div className={`text-xs font-black ${makatsuOn ? 'text-rose-300' : 'text-[#94a3b8]'}`}>
                                {makatsuOn ? 'Attivo — segnali eseguiti in automatico' : 'Spento — nessuna apertura automatica'}
                              </div>
                              <div className="text-[10px] text-[#64748b] mt-0.5 leading-snug">
                                Apre in automatico con regole standard (zona prezzo ±2$).
                              </div>
                            </div>
                            <Switch
                              checked={makatsuOn}
                              disabled={savingMakatsu}
                              onCheckedChange={handleToggleMakatsu}
                            />
                          </div>

                          <div className="text-[10px] text-[#64748b] leading-snug">
                            SL/TP delle aperture automatiche Makatsu su questa coppia (default: SL 1,2% · TP 1,6%).
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">SL % (1 – 1,2)</label>
                              <input
                                type="number" min={1} max={1.2} step={0.05}
                                value={makatsuSl}
                                onChange={e => setMakatsuSl(e.target.value)}
                                placeholder="Default 1,2"
                                className={`w-full bg-[#0a0f1e] border rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none transition-colors ${makatsuSlError ? 'border-red-500 focus:border-red-500' : 'border-[#1e3a5f] focus:border-[#38bdf8]'}`}
                              />
                              {makatsuSlError && <div className="text-[10px] text-red-400 mt-1">{makatsuSlError}</div>}
                            </div>
                            <div>
                              <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">TP % (1 – 4)</label>
                              <input
                                type="number" min={1} max={4} step={0.1}
                                value={makatsuTp}
                                onChange={e => setMakatsuTp(e.target.value)}
                                placeholder="Default 1,6"
                                className={`w-full bg-[#0a0f1e] border rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none transition-colors ${makatsuTpError ? 'border-red-500 focus:border-red-500' : 'border-[#1e3a5f] focus:border-[#38bdf8]'}`}
                              />
                              {makatsuTpError && <div className="text-[10px] text-red-400 mt-1">{makatsuTpError}</div>}
                            </div>
                          </div>
                          <div className="text-[10px] text-[#64748b] leading-snug">
                            Vuoto = default Makatsu del sistema (SL 1,2% · TP 1,6%).
                          </div>
                        </div>
                      )}

                      <button
                        onClick={handleSaveAutoPrefs}
                        disabled={savingPrefs || hasAutoPrefsErrors}
                        className="w-full py-2.5 rounded-xl font-black text-sm bg-[#1e3a5f] border border-[#38bdf8]/40 text-[#38bdf8] hover:bg-[#1e4a7f] disabled:opacity-40 transition-all"
                      >
                        {savingPrefs ? 'Salvataggio...' : hasAutoPrefsErrors ? 'Correggi i valori fuori range' : 'Salva personalizzazione auto (SL/TP + finestra)'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* ── Tab: Token EA ── */}
              {tab === 'token' && (
                <div className="space-y-4">
                  <div className="bg-[#0a0f1e] rounded-xl border border-[#1e3a5f]/60 px-4 py-3 space-y-1">
                    {newTokens ? (
                      <>
                        <div className="text-[10px] text-yellow-400 font-bold uppercase tracking-wider mb-2">Nuovi Token Generati — Copia Adesso</div>
                        <TokenRow label="Token PROP" value={newTokens.prop_token ? newTokens.prop_token.substring(0, 20) + '...' : null} full={newTokens.prop_token} />
                        <TokenRow label="Token BROKER" value={newTokens.broker_token ? newTokens.broker_token.substring(0, 20) + '...' : null} full={newTokens.broker_token} />
                        <div className="text-[9px] text-red-400/80 mt-1">⚠ Questi token non sono recuperabili successivamente. Aggiornali subito negli EA.</div>
                      </>
                    ) : (
                      <>
                        <TokenRow label="Token PROP" value={detail.prop_token} full={detail.prop_token_full} />
                        <TokenRow label="Token BROKER" value={detail.broker_token} full={detail.broker_token_full} />
                      </>
                    )}
                    <TokenRow label="Cloud URL" value={detail.cloud_url} full={detail.cloud_url} />
                  </div>

                  <div className="bg-[#1a0f0f] border border-red-900/50 rounded-xl p-4 space-y-3">
                    <div className="text-[11px] font-black text-red-400 uppercase tracking-wider">Rigenera Token</div>
                    <p className="text-[11px] text-[#94a3b8]">
                      Genera nuovi token per PROP e BROKER. Gli EA attuali riceveranno un errore 401 al prossimo aggiornamento e dovranno essere riconfigurati manualmente.
                    </p>
                    {!regenConfirm ? (
                      <button
                        onClick={() => setRegenConfirm(true)}
                        className="w-full py-2 rounded-lg border border-red-700 bg-red-900/20 text-red-400 text-xs font-black hover:bg-red-900/40 transition-all"
                      >
                        Rigenera Token EA
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-[10px] text-red-400 font-bold">Sei sicuro? Questa azione è irreversibile.</p>
                        <div className="flex gap-2">
                          <button onClick={() => setRegenConfirm(false)} className="flex-1 py-2 rounded-lg border border-[#1e3a5f] text-[#64748b] text-xs font-bold hover:bg-[#1e3a5f]/30 transition-all">Annulla</button>
                          <button onClick={handleRegenerateTokens} disabled={saving} className="flex-1 py-2 rounded-lg bg-red-900/50 border border-red-700 text-red-300 text-xs font-black hover:bg-red-900/70 disabled:opacity-40 transition-all">
                            {saving ? 'Rigenerazione...' : 'Conferma Rigenera'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Tab: Elimina ── */}
              {tab === 'delete' && (
                <div className="space-y-4">
                  <div className="bg-[#1a0f0f] border border-red-900/50 rounded-xl p-4 space-y-2">
                    <div className="text-[11px] font-black text-red-400 uppercase tracking-wider">⚠ Attenzione</div>
                    <p className="text-[11px] text-[#94a3b8]">
                      Chiudi prima tutte le posizioni aperte su entrambi i terminali. Se ci sono operazioni hedge attive, l'eliminazione verrà bloccata.
                    </p>
                    <p className="text-[11px] text-[#94a3b8]">
                      I token EA associati verranno revocati e gli EA riceveranno un errore al prossimo aggiornamento.
                    </p>
                  </div>

                  <div>
                    <label className="block text-[10px] text-[#64748b] font-bold uppercase tracking-wider mb-1">
                      Scrivi il nome esatto della coppia per confermare
                    </label>
                    <div className="text-[11px] text-[#38bdf8] font-mono mb-2">{detail.display_name}</div>
                    <input
                      type="text"
                      value={deleteConfirmName}
                      onChange={e => setDeleteConfirmName(e.target.value)}
                      placeholder={detail.display_name}
                      className="w-full bg-[#0a0f1e] border border-red-900/50 rounded-lg px-3 py-2 text-sm text-white placeholder-[#334155] focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>

                  <button
                    onClick={handleDelete}
                    disabled={deleteConfirmName !== detail.display_name || deleting}
                    className="w-full py-3 rounded-xl font-black text-sm bg-red-900/30 border border-red-700 text-red-400 hover:bg-red-900/50 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
                  >
                    {deleting ? 'Eliminazione...' : `Elimina ${detail.display_name}`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer save button (only for tab connection and propfirm) */}
        {(tab === 'connection' || tab === 'propfirm') && !loading && detail && (
          <div className="p-4 border-t border-[#1e3a5f]">
            <button
              onClick={tab === 'connection' ? handleSaveConnection : handleSavePropFirm}
              disabled={saving}
              className="w-full py-2.5 rounded-xl font-black text-sm bg-[#1e3a5f] border border-[#38bdf8]/40 text-[#38bdf8] hover:bg-[#1e4a7f] disabled:opacity-40 transition-all"
            >
              {saving ? 'Salvataggio...' : 'Salva Modifiche'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
